import { Controller } from "react-hook-form";
import {
  FormControl,
  Box,
  FormHelperText,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  ConfigurationItemBase,
  ConfigurationList,
  ConfigurationPaper,
} from "~/components/configuration-items";
import { useSpaceSettingsForm } from "./hooks/SpaceSettingsForm";
import { useCanUpdateSpace } from "~/lib/iam/actions/mondoo/captain/Captain/UpdateSpace";
import { SpaceScope } from "~/hooks/useScope";
import { FieldLoadingIcon } from "~/components/Form/components/FieldLoadingIcon";

export type ExceptionsSettingsProps = {
  space: SpaceScope;
};

export function ExceptionsSettings({ space }: ExceptionsSettingsProps) {
  const canUpdateSpace = useCanUpdateSpace(space);
  const { form, onSubmit, fieldIsUpdating } = useSpaceSettingsForm({
    spaceMrn: space.mrn,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isLoading },
  } = form;

  return (
    !isLoading && (
      <Box component="form">
        <ConfigurationPaper>
          <ConfigurationList
            title="Exceptions"
            sx={{
              "& .MuiListItemSecondaryAction-root": {
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: "24px",
              },
            }}
          >
            <ConfigurationItemBase
              primary="Require exception approvals"
              secondary="Require approval of exceptions before the exceptions take effect."
            >
              <FormControl fullWidth>
                <Controller
                  name="exceptionsRequireApproval"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(event, checked) => {
                            field.onChange(event, checked);
                            handleSubmit(onSubmit)();
                          }}
                          disabled={
                            !canUpdateSpace ||
                            fieldIsUpdating("exceptionsRequireApproval")
                          }
                          size="small"
                        />
                      }
                      label={
                        <>
                          {fieldIsUpdating("exceptionsRequireApproval") && (
                            <FieldLoadingIcon />
                          )}
                          {field.value ? "On" : "Off"}
                        </>
                      }
                      labelPlacement="start"
                    />
                  )}
                />
                {errors.experimentalSources && (
                  <FormHelperText error>
                    {errors.experimentalSources.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
            <ConfigurationItemBase
              primary="Allow non-expiring exceptions"
              secondary="Allow exceptions to be created with an indefinite duration that does not require future review."
            >
              <FormControl fullWidth>
                <Controller
                  name="exceptionsAllowIndefiniteValidUntil"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(event, checked) => {
                            field.onChange(event, checked);
                            handleSubmit(onSubmit)();
                          }}
                          disabled={
                            !canUpdateSpace ||
                            fieldIsUpdating(
                              "exceptionsAllowIndefiniteValidUntil",
                            )
                          }
                          size="small"
                        />
                      }
                      label={
                        <>
                          {fieldIsUpdating(
                            "exceptionsAllowIndefiniteValidUntil",
                          ) && <FieldLoadingIcon />}
                          {field.value ? "On" : "Off"}
                        </>
                      }
                      labelPlacement="start"
                    />
                  )}
                />
                {errors.exceptionsAllowIndefiniteValidUntil && (
                  <FormHelperText error>
                    {errors.exceptionsAllowIndefiniteValidUntil.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
            <ConfigurationItemBase
              primary="Allow users to approve their own exceptions"
              secondary="Allow users to approve their own exceptions if they have the appropriate role."
            >
              <FormControl fullWidth>
                <Controller
                  name="exceptionsAllowSelfApproval"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(event, checked) => {
                            field.onChange(event, checked);
                            handleSubmit(onSubmit)();
                          }}
                          disabled={
                            !canUpdateSpace ||
                            fieldIsUpdating("exceptionsAllowSelfApproval")
                          }
                          size="small"
                        />
                      }
                      label={
                        <>
                          {fieldIsUpdating("exceptionsAllowSelfApproval") && (
                            <FieldLoadingIcon />
                          )}
                          {field.value ? "On" : "Off"}
                        </>
                      }
                      labelPlacement="start"
                    />
                  )}
                />
                {errors.exceptionsAllowSelfApproval && (
                  <FormHelperText error>
                    {errors.exceptionsAllowSelfApproval.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
          </ConfigurationList>
        </ConfigurationPaper>
      </Box>
    )
  );
}
